import React, {useCallback, useContext, useEffect, useState} from 'react'
import {FormActionsContext, FormValidationContext} from '@modifi/plugin-forms'
import {Context as UploadManagerContext} from '@modifi/plugin-documents'
import {useSelector} from '@modifi/redux'
import {selectCompanyIsLocked} from '../../../../data/company'
import FormFooter from './FormCtaFooter'
import CustomFormCtaFooter from './CustomFormCtaFooter'
import {CustomFormCtaFooterProps, FormContextDerivedProps} from './types'

/**
 * Everything the form footer needs except the props this component reads off the form context
 * itself.
 */
type ConnectedFormFooterProps = Omit<CustomFormCtaFooterProps, FormContextDerivedProps> & {
  hasCustomFormFooter?: boolean
}

const ConnectedFormFooter: React.FC<ConnectedFormFooterProps> = ({
  hasCustomFormFooter = false,
  ...restProps
}) => {
  const {loading, disableSubmit} = restProps
  const {submit, discard, canSubmit, hasChanges, validationPending} = useContext(FormActionsContext)
  const {isUploading} = useContext(UploadManagerContext)
  const {formIsValid} = useContext(FormValidationContext)
  const isCompanyLocked = useSelector(selectCompanyIsLocked)

  /* The lock status is polled, so the company can get locked while the user still has unsaved
  changes. The fields become read-only then and these changes must not be submitted anymore, while
  moving between steps without changes stays possible */
  const isBlockedByCompanyLock = isCompanyLocked && hasChanges

  const Footer: React.FC<CustomFormCtaFooterProps> = hasCustomFormFooter
    ? CustomFormCtaFooter
    : FormFooter

  const [userIntendedSubmit, setUserIntendedSubmit] = useState(false)

  const handleSubmitClick = useCallback(() => {
    setUserIntendedSubmit(true)
  }, [])

  useEffect(() => {
    // in case the user wants to submit
    if (userIntendedSubmit && !validationPending) {
      if (canSubmit && !isBlockedByCompanyLock && submit) {
        submit()
      }
      setUserIntendedSubmit(false)
    }
  }, [userIntendedSubmit, validationPending, canSubmit, isBlockedByCompanyLock, submit])

  return (
    <Footer
      {...restProps}
      lockNavigation={!loading && hasChanges}
      discardChanges={discard}
      onSubmit={handleSubmitClick}
      hasUnsavedChanges={hasChanges}
      canSubmit={
        canSubmit &&
        !isUploading &&
        !userIntendedSubmit &&
        !disableSubmit &&
        !isBlockedByCompanyLock
      }
      formIsValid={formIsValid}
    />
  )
}

export default ConnectedFormFooter
